/* <search-panel> — the site search (snippets/header-search.liquid, styles in search.css).

   Opens from any .fire-search element (header, mobile menu) and from the "/" key; closes on Esc, the dimmed page,
   «Закрити» / «Скасувати», and when the menu or the cart is opened. Desktop: a panel under the header
   (--sp-top = the header's bottom edge); below 1024px: full screen.
   Views (root data-view): empty → results → none. Live results come from sections/predictive-search through the
   Section Rendering API 250 ms after the last keystroke; the old results stay (faded) until the new ones arrive and
   a thin line runs under the field meanwhile. When Shopify has only fuzzy guesses (data-relevant="false") the query
   is retyped in the other keyboard layout ("тшлу" → "nike", "rhjcsdrb" → "кросівки"); if that matches, its
   results are shown with a note, otherwise the "nothing found" view.
   Out-of-stock products: data-unavailable (Theme settings → Search) = hide | last | show.
   «Ви шукали» = localStorage "uniform:recent-searches" (last 5), saved on Enter and on a click in the results.
   ↑ / ↓ move through suggestions, collections and cards, Enter opens the highlighted one. */
(() => {
  if (customElements.get('search-panel')) return;

  const RECENT_KEY = 'uniform:recent-searches';
  const RECENT_MAX = 5;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;

  // ЙЦУКЕН ↔ QWERTY, Ukrainian first; ы / э / ъ / ё are the Russian keys on the same places
  const CYR = 'йцукенгшщзхїфівапролджєячсмитьбюыэъё';
  const LAT = "qwertyuiop[]asdfghjkl;'zxcvbnm,.s']`";
  const toLat = new Map([...CYR].map((c, i) => [c, LAT[i]]));
  const toCyr = new Map([...LAT.slice(0, 32)].map((c, i) => [c, CYR[i]]));
  const HAS_CYR = /[а-яіїєґё]/i;
  const ONLY_LAT = /^[a-z\s[\];',.`]+$/i;

  function otherLayout(q) {
    const low = q.toLowerCase();
    if (HAS_CYR.test(low)) return [...low].map((c) => toLat.get(c) ?? c).join('');
    if (ONLY_LAT.test(low)) return [...low].map((c) => toCyr.get(c) ?? c).join('');
    return '';
  }

  function readRecent() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
    } catch (e) { return []; }
  }
  function writeRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (e) { /* private mode */ }
  }

  class SearchPanel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.input = this.querySelector('.sp-input');
      this.form = this.querySelector('.sp-bar');
      this.clearBtn = this.querySelector('[data-sp-clear]');
      this.views = Object.fromEntries([...this.querySelectorAll('[data-sp-view]')].map((v) => [v.dataset.spView, v]));
      this.status = this.querySelector('[data-sp-status]');
      this.live = this.dataset.live === 'true';
      this.cache = new Map();
      this.token = 0;
      this.active = -1;

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('.fire-search');
        if (opener && !this.contains(opener)) { e.preventDefault(); this.open(opener); return; }
        if (!this.hidden && e.target.closest('.carto, .fire-menu')) this.close(false);
      });
      document.addEventListener('keydown', (e) => {
        if (!this.hidden && e.key === 'Escape') { e.preventDefault(); this.close(); return; }
        if (this.hidden && e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !this.isTyping(e.target)) {
          e.preventDefault();
          this.open();
        }
      });
      window.addEventListener('resize', () => { if (!this.hidden) this.placeUnderHeader(); });

      this.addEventListener('click', (e) => this.onClick(e));
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.form.addEventListener('submit', (e) => {
        let q = this.input.value.trim();
        if (!q) { e.preventDefault(); return; }
        // the results on screen were for the retyped query ("тшлу" → "nike"): open the page for that one
        if (this.fixedFor && this.fixedFor.q === q) { q = this.fixedFor.fixed; this.input.value = q; }
        this.remember(q);
      });
    }

    isTyping(el) {
      return !!(el && (el.isContentEditable || /^(input|textarea|select)$/i.test(el.tagName)));
    }

    /* ---------- open / close ---------- */
    open(trigger) {
      if (!this.hidden) { this.input.focus(); return; }
      this.trigger = trigger || document.activeElement;
      this.placeUnderHeader();
      this.hidden = false;
      document.documentElement.classList.add('sp-open');
      document.body.classList.remove('open-menu');
      // focus inside the click handler so phones raise the keyboard
      this.input.focus({ preventScroll: true });
      this.renderRecent();
      if (this.query().length >= MIN_CHARS) this.run(); else this.show('empty');
    }

    close(restoreFocus = true) {
      if (this.hidden) return;
      this.token++;
      clearTimeout(this.timer);
      this.hidden = true;
      this.removeAttribute('data-loading');
      document.documentElement.classList.remove('sp-open');
      this.input.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }

    placeUnderHeader() {
      const header = document.getElementById('header');
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty('--sp-top', `${bottom}px`);
    }

    /* ---------- typing ---------- */
    query() { return this.input.value.trim(); }

    onInput() {
      this.clearBtn.hidden = this.input.value.length === 0;
      clearTimeout(this.timer);
      if (this.query().length < MIN_CHARS) {
        this.token++;
        this.removeAttribute('data-loading');
        this.show('empty');
        return;
      }
      if (!this.live) return;
      this.timer = setTimeout(() => this.run(), DEBOUNCE_MS);
    }

    async fetchResults(q) {
      if (this.cache.has(q)) return this.cache.get(q);
      const p = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
        'resources[options][unavailable_products]': this.dataset.unavailable || 'show'
      });
      const html = await fetch(`${this.dataset.suggestUrl}?${p}`, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error(`search ${r.status}`);
        return r.text();
      });
      const box = new DOMParser().parseFromString(html, 'text/html').getElementById('sp-results');
      if (box) this.cache.set(q, box);
      return box;
    }

    async run() {
      const q = this.query();
      if (q.length < MIN_CHARS || !this.live) return;
      const token = ++this.token;
      this.setAttribute('data-loading', '');
      let box = null;
      let fixed = '';
      try {
        box = await this.fetchResults(q);
        if (token !== this.token) return;
        if (!box || box.dataset.relevant !== 'true') {
          const alt = otherLayout(q);
          if (alt && alt !== q.toLowerCase()) {
            const altBox = await this.fetchResults(alt);
            if (token !== this.token) return;
            if (altBox && altBox.dataset.relevant === 'true') { box = altBox; fixed = alt; }
          }
        }
      } catch (e) {
        if (token !== this.token) return;
        box = null;
      }
      this.removeAttribute('data-loading');
      if (box && box.dataset.relevant === 'true') this.showResults(box, fixed);
      else this.showNone(q);
    }

    showResults(box, fixed) {
      this.fixedFor = fixed ? { q: this.query(), fixed } : null;
      const view = this.views.results;
      const node = box.cloneNode(true);
      if (fixed) {
        const note = document.createElement('p');
        note.className = 'sp-fix';
        note.innerHTML = 'Результати для <b></b><span>· розкладку виправлено</span>';
        note.querySelector('b').textContent = `«${fixed}»`;
        node.prepend(note);
      }
      view.replaceChildren(node);
      this.show('results');
      view.classList.remove('is-in'); void view.offsetWidth; view.classList.add('is-in');
      const n = +box.dataset.count || 0;
      this.status.textContent = n ? `Знайдено товарів: ${n}` : 'Є підказки, товарів немає';
    }

    showNone(q) {
      this.views.none.querySelectorAll('[data-sp-term]').forEach((el) => { el.textContent = q; });
      this.show('none');
      this.status.textContent = `За запитом «${q}» нічого не знайдено`;
    }

    show(name) {
      Object.entries(this.views).forEach(([key, el]) => { el.hidden = key !== name; });
      this.dataset.view = name;
      this.input.setAttribute('aria-expanded', String(name === 'results'));
      this.setActive(-1);
    }

    /* ---------- keyboard ---------- */
    options() {
      const view = this.views[this.dataset.view];
      return view ? [...view.querySelectorAll('[data-sp-opt], .sp-pop__a, .sp-recent__a')].filter((el) => el.offsetParent !== null) : [];
    }

    setActive(i) {
      const opts = this.options();
      this.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
      this.active = i;
      const el = opts[i];
      if (!el) { this.input.removeAttribute('aria-activedescendant'); return; }
      if (!el.id) el.id = `sp-opt-${Math.random().toString(36).slice(2, 8)}`;
      el.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      const opts = this.options();
      if (e.key === 'Enter') {
        if (this.active >= 0 && opts[this.active]) { e.preventDefault(); opts[this.active].click(); }
        return;
      }
      if (!opts.length) return;
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? this.active + 1 : this.active - 1;
      this.setActive(next < -1 ? opts.length - 1 : next >= opts.length ? -1 : next);
    }

    /* ---------- clicks inside ---------- */
    onClick(e) {
      if (e.target.closest('[data-sp-close]')) { this.close(); return; }
      if (e.target.closest('[data-sp-clear]')) {
        this.input.value = '';
        this.onInput();
        this.input.focus();
        return;
      }
      if (e.target.closest('[data-sp-recent-clear]')) { writeRecent([]); this.renderRecent(); return; }
      const drop = e.target.closest('.sp-recent__x');
      if (drop) {
        const term = drop.parentElement.querySelector('.sp-recent__a').dataset.spFill;
        writeRecent(readRecent().filter((x) => x.toLowerCase() !== term.toLowerCase()));
        this.renderRecent();
        return;
      }
      const fill = e.target.closest('[data-sp-fill]');
      if (fill && !e.metaKey && !e.ctrlKey && !e.shiftKey && this.live) {
        e.preventDefault();
        this.input.value = fill.dataset.spFill;
        this.clearBtn.hidden = false;
        this.input.focus();
        this.run();
        return;
      }
      const link = e.target.closest('a[href]');
      if (link && this.dataset.view === 'results') this.remember(this.fixedFor ? this.fixedFor.fixed : this.query());
    }

    /* ---------- «Ви шукали» ---------- */
    remember(q) {
      q = (q || '').trim();
      if (q.length < MIN_CHARS) return;
      writeRecent([q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())]);
    }

    renderRecent() {
      const box = this.querySelector('[data-sp-recent]');
      const list = this.querySelector('[data-sp-recent-list]');
      const tpl = this.querySelector('[data-sp-recent-tpl]');
      if (!box || !list || !tpl) return;
      const items = readRecent();
      list.replaceChildren(...items.map((q) => {
        const li = tpl.content.firstElementChild.cloneNode(true);
        const a = li.querySelector('.sp-recent__a');
        a.textContent = q;
        a.dataset.spFill = q;
        a.href = `${this.dataset.searchUrl}?options%5Bprefix%5D=last&options%5Bunavailable_products%5D=${this.dataset.unavailable || 'show'}&type=product&q=${encodeURIComponent(q)}`;
        return li;
      }));
      box.hidden = items.length === 0;
    }
  }

  customElements.define('search-panel', SearchPanel);
})();
